<template>
  <div
    v-if="shouldShowBanner"
    class="bg-blue-50 border-b border-blue-200 px-4 py-3"
  >
    <div class="container-lg">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-blue-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-blue-800">
              <span class="font-medium">Complete your profile</span>
              to get personalized teacher recommendations and start booking
              lessons.
            </p>
          </div>
        </div>
        <div class="flex items-center space-x-3">
          <router-link to="/onboarding" class="btn-primary text-xs px-3 py-1">
            Complete Setup
          </router-link>
          <button
            @click="dismissBanner"
            class="flex-shrink-0 text-blue-400 hover:text-blue-600"
          >
            <svg
              class="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { currentUser } from "@/services/auth.service";
import { hasCompletedOnboarding } from "@/services/studentProfile.service";

export default {
  name: "OnboardingBanner",
  setup() {
    const isVisible = ref(true);
    const onboardingCompleted = ref(true); // Default to true to hide banner initially

    const shouldShowBanner = computed(() => {
      return (
        isVisible.value &&
        currentUser.user &&
        !currentUser.user.isTeacher &&
        !onboardingCompleted.value
      );
    });

    const checkOnboardingStatus = async (user) => {
      // Reset onboarding status to true (hidden) if no user or user is teacher
      if (!user || user.isTeacher) {
        onboardingCompleted.value = true;
        return;
      }

      // Only check onboarding status for authenticated non-teacher users
      try {
        const completed = await hasCompletedOnboarding(user.uid);
        onboardingCompleted.value = completed;
      } catch (error) {
        console.error("Error checking onboarding status:", error);
        onboardingCompleted.value = true; // Hide banner on error
      }
    };

    const dismissBanner = () => {
      isVisible.value = false;
      // Store dismissal in localStorage so it doesn't show again this session
      try {
        localStorage.setItem("onboardingBannerDismissed", "true");
      } catch {
        // localStorage unavailable (e.g. sandboxed iframe) — dismissal just won't persist
      }
    };

    // Watch for authentication state changes using the reactive currentUser from auth service
    watch(
      () => currentUser.user,
      async (newUser) => {
        if (newUser) {
          await checkOnboardingStatus(newUser);
        } else {
          onboardingCompleted.value = true; // Hide banner when no user
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      // Check if banner was already dismissed this session
      try {
        const dismissed = localStorage.getItem("onboardingBannerDismissed");
        if (dismissed) {
          isVisible.value = false;
        }
      } catch {
        // localStorage unavailable (e.g. sandboxed iframe) — ignore
      }
    });

    return {
      shouldShowBanner,
      dismissBanner,
    };
  },
};
</script>
